import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import ProofOfWork from "../components/ProofOfWork";
import FAQSection from "../components/FAQSection";
import bonkLogo from "../assets/logos/bonk.png";
import solanaLogo from "../assets/logos/solana.png"; // or .svg depending on what you saved
import phantomLogo from "../assets/logos/phantom.png";
import raydiumLogo from "../assets/logos/raydium.png";
import cointelegraphLogo from "../assets/logos/cointelegraph.png";
import StatsSection from "../components/StatsSection";


// Mosaic official blue from your branding (used for the points)
const mosaicBlue = "#3498DB";

// -----------------------------------------------------------
// 1. THREE.JS BACKGROUND POINTS (Continuous Rotation)
// -----------------------------------------------------------

function BackgroundPoints() {
  const pointsRef = useRef();

  // Generate points once
  const geometry = useMemo(() => {
    const points = [];
    for (let i = 0; i < 1000; i++) {
      points.push(
        new THREE.Vector3(
          (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 50
        )
      );
    }
    return new THREE.BufferGeometry().setFromPoints(points);
  }, []);

  // Rotate the cloud slowly (continuous animation)
  useFrame(() => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += 0.0008;
      pointsRef.current.rotation.x += 0.0003;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry attach="geometry" {...geometry} />
      <pointsMaterial attach="material" color={mosaicBlue} size={0.05} />
    </points>
  );
}

// -----------------------------------------------------------
// 2. MAIN PAGE COMPONENT
// -----------------------------------------------------------
const packages = [
  {
    name: "Shrimp",
    price: "$750 / month",
    features: [
      "Up to 70 likes",
      "20 retweets",
      "15 custom replies",
      "Up to 5K Views",
    ],
    cta: "Start Small",
  },
  {
    name: "Dolphin",
    price: "$1500 / month",
    features: [
      "Up to 130 likes",
      "30+ retweets",
      "20 custom replies",
      "Up to 8K Views",
    ],
    cta: "Choose Mid",
  },
  {
    name: "Whale",
    price: "$2300 / month",
    features: [
      "Up to 300 likes",
      "Up to 100 retweets",
      "60 custom replies",
      "Up to 30K Views",
    ],
    cta: "Go Big",
  },
  // add more if needed
];


const MosaicAgency = () => {
  const sectionRefs = useRef([]);

  return (
    <>
      <style>
        {`
        
        :root {
          --color-primary: #E5E7EB; 
          --color-secondary: #54595F; 
          --color-text: #7A7A7A; 
          --color-accent: #3498DB; 
          --color-accent-hover: #1A5276; 
          --color-bg: #000000; 
          --color-section-bg: #0b0f0d; 
          --color-card-bg: #151515; 
          --font-primary: "Montserrat", sans-serif;
        }

        body {
          background-color: var(--color-bg) !important; 
          font-family: var(--font-primary) !important;
          color: var(--color-primary) !important;
          margin: 0;
          padding: 0;
        }

        .mosaic-agency {
          position: relative;
          width: 100%;
          min-height: 100vh;
          background-color: var(--color-bg);
          color: var(--color-primary);
          overflow-x: hidden;
        }

        .background-canvas {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1; /* behind content */
        }

        .hero {
          position: relative;
          z-index: 1;
          padding: 10rem 2rem 4rem;
          text-align: center;
          background: transparent; /* Allows canvas background to show */
        }

        .hero h1 {
            font-size: 3rem;
            font-weight: 800;
            color: #fff;
        }

        .hero p {
          color: #fff;
        }

        .section {
          position: relative;
          z-index: 1;
          padding: 8rem 2rem;
          max-width: 800px;
          margin: 0 auto;
          background: transparent;
        }

        .how-it-works-section {
          background-color: var(--color-section-bg);
          padding: 120px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 80px;
          background: rgba(11, 15, 13, 0.95);
          border-radius: 16px;
          margin: 40px auto;
          box-shadow: 0 0 50px rgba(0, 0, 0, 0.5);
        }

        .features-container {
            display: flex;
            justify-content: space-around;
            gap: 40px;
            max-width: 1200px;
        }

        .feature {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          color: var(--color-primary);
          max-width: 300px;
        }

        .feature-icon {
          color: var(--color-accent);
          margin-bottom: 24px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .feature-icon svg {
            stroke: var(--color-accent); 
            fill: none;
        }

        .feature-icon:hover {
          transform: translateY(-6px);
          box-shadow: 0 0 12px var(--color-accent); 
        }

        .feature-title {
          font-size: 38px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .feature-desc {
          font-size: 16px;
          color: var(--color-secondary);
        }

        .trusted-by-section {
          padding: 6rem 2rem;
          text-align: center;
          background: transparent;
          margin: 4rem 0;
          overflow: hidden;
        }

        .trusted-title {
          font-size: 2rem;
          margin-bottom: 3rem;
          color: var(--color-primary);
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        .trusted-logos-container {
          overflow: hidden;
          position: relative;
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
        }

        .trusted-logos {
          display: flex;
          animation: scroll-logos 25s linear infinite;
          gap: 4rem;
          padding: 0 2rem;
          align-items: center;
        }

        .trusted-item {
          flex-shrink: 0;
          padding: 1.5rem 2rem;
          background: rgba(255, 255, 255, 0.05); 
          border: 1px solid rgba(52, 152, 219, 0.2);
          border-radius: 16px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          min-width: 150px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .trusted-item img {
          max-width: 120px;
          max-height: 50px;
          width: auto;
          height: auto;
          filter: brightness(0) invert(1) grayscale(100%); 
          transition: all 0.3s ease;
        }

        .trusted-item:hover {
          background: var(--color-accent);
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 10px 25px rgba(52, 152, 219, 0.5);
        }

        .trusted-item:hover img {
          filter: brightness(1) invert(0);
          transform: scale(1.1);
        }

        @keyframes scroll-logos {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(-150px * 6 - 4rem * 6)); }
        }

        .trusted-logos-container:hover .trusted-logos {
          animation-play-state: paused;
        }

        @media (max-width: 768px) {
          .features-container {
            flex-direction: column;
            gap: 40px;
          }
          .trusted-by-section { padding: 4rem 1rem; }
          .trusted-item { padding: 1rem 1.5rem; min-width: 120px; height: 60px; }
          .trusted-item img { max-width: 90px; max-height: 40px; }
          .trusted-logos { gap: 2rem; }

          @keyframes scroll-logos {
            0% { transform: translateX(0); }
            100% { transform: translateX(calc(-120px * 6 - 2rem * 6)); }
          }

          .hero { padding: 6rem 1rem 2rem; }
          .section { padding: 4rem 1rem; }
          .how-it-works-section { padding: 60px 20px; gap: 40px; }
          .feature-title { font-size: 32px; }
        }
        `}
      </style>

      <div className="mosaic-agency">
        <Canvas
          className="background-canvas"
          camera={{ position: [0, 0, 5] }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: -1,
          }}
        >
          <ambientLight intensity={0.5} />
          <BackgroundPoints />
        </Canvas>

       <header className="hero">
  <h1>
    <span className="mota-red">MOTA</span>-MOSAIC OnChain Task Agency
  </h1>
  <p>
    Exclusive human engagement for X. Break through the algorithm with a curated
    network of crypto native people who react, reply, and amplify in ways that
    spark traction.
  </p>
</header>

<StatsSection />

       <section className="trusted-by-section">
  <h2 className="trusted-title">Trusted by</h2>
  <div className="trusted-logos-container">
    <div className="trusted-logos">
      <div className="trusted-item">
        <img src={bonkLogo} alt="Bonk" />
      </div>
      <div className="trusted-item">
        <img src={solanaLogo} alt="Solana" />
      </div>
      <div className="trusted-item">
        <img src={phantomLogo} alt="Phantom" />
      </div>
      <div className="trusted-item">
        <img src={raydiumLogo} alt="Raydium" />
      </div>
      <div className="trusted-item">
        <img src={cointelegraphLogo} alt="CoinTelegraph" />
      </div>
    </div>
  </div>
</section>


        {/* <section
          ref={(el) => (sectionRefs.current[0] = el)}
          className="section"
          id="services"
        >
          <h2>Services</h2>
          <p>
            Smart contracts, memecoin launches, NFT minting, security audits,
            community raids… (fill in your copy here)
          </p>
        </section> */}

        <section
          ref={(el) => (sectionRefs.current[2] = el)}
          className="section"
          id="why-us"
        >
          <h2>Why Choose MOTA</h2>
          <p>
            On X, the algorithm rewards noise, not substance. Most creators
            drown in the feed, no matter how good their content is. Paid ads?
            Expensive and ignored. Bots? Obvious and worthless. That's where The
            Black Book comes in. We built a network of trenchers, traders and
            crypto-natives who not only understand the culture and rhythm of X
            but also connect with each other and unite. We exist for creators
            who are tired of shouting into the void. With us, you don't just get
            engagement - you get traction, visibility, and community momentum.
            Real people. Real conversations. Real growth.
          </p>

          {/* ✅ Proof of Work slider goes here */}
          <ProofOfWork />

    {/* after <ProofOfWork /> */}
<>
  <style>{`
.hero h1 {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #ffffff; /* white for the default text */
}

.mota-red {
  color: #ff0000; /* shiny MOTA red */
  font-weight: 800;
}


.offer-section {
  display: flex;
  justify-content: center;
  padding: 6rem 2rem;
  background: #000; /* dark background */
}

.offer-card {
  max-width: 700px;
  width: 100%;
  background: linear-gradient(180deg, rgba(0,102,204,0.15), #000); 
  border: 1px solid rgba(0, 153, 255, 0.4);
  border-radius: 20px;
  padding: 3rem 2rem;
  color: #fff;
  text-align: center;
  box-shadow: 0 0 30px rgba(0,153,255,0.15);
}

.offer-subtitle {
  font-size: 1rem;
  font-weight: 700;
  color: #00aaff; /* mosaic accent */
  margin-bottom: 1rem;
  text-transform: uppercase;
}

.offer-title {
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #00ccff; /* mosaic title blue */
}

.offer-price {
  font-size: 2rem;
  font-weight: 700;
  color: #00ccff; /* mosaic price blue */
  margin-bottom: 1rem;
}

.offer-text {
  font-size: 1.1rem;
  color: #bbb;
  margin-bottom: 2rem;
}

.offer-list {
  text-align: left;
  list-style: none;
  padding: 0;
  margin: 0 auto 2rem;
  max-width: 600px;
  font-size: 1rem;
  line-height: 1.6;
}

.offer-list li {
  margin-bottom: 0.8rem;
}

.offer-button {
  background: #00aaff;
  color: #fff;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 10px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.3s ease;
}

.offer-button:hover {
  background: #0088cc;
  transform: translateY(-3px);
}

@media (max-width: 768px) {
  .offer-title {
    font-size: 1.6rem;
  }
  .offer-price {
    font-size: 1.6rem;
  }
  .offer-text {
    font-size: 1rem;
  }
}

  
.demo-section {
  position: relative;
  z-index: 1;
  padding: 8rem 2rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  max-width: 900px;
  margin: 6rem auto;
}

.demo-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 2rem;
  color: #fff;
}

.demo-text {
  font-size: 1.2rem;
  color: #bbb;
  line-height: 1.6;
  margin-bottom: 3rem;
}

.demo-button {
  background: #3498DB;
  color: #fff;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 10px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.3s ease;
}

.demo-button:hover {
  background: #1A5276;
  transform: translateY(-3px);
}

@media (max-width: 768px) {
  .demo-title {
    font-size: 2rem;
  }
  .demo-text {
    font-size: 1rem;
  }
  .demo-button {
    font-size: 1rem;
    padding: 0.8rem 2rem;
  }
}


 .packages-section {
  position: relative;
  z-index: 1;
  padding: 8rem 2rem;
  text-align: center;
  background: transparent;
}

.packages-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 4rem;
  color: #fff;
}

.package-cards {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
}

.package-card {
  background: rgba(255, 255, 255, 0.05); /* glass */
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 3rem 2rem;
  width: 320px;
  cursor: pointer;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}

.package-card:hover {
  transform: translateY(-8px) scale(1.03);
  box-shadow: 0 10px 30px rgba(52, 152, 219, 0.4); /* mosaic blue glow */
}

.package-card h3 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #fff;
}

.package-card .price {
  font-size: 1.5rem;
  color: #3498DB;
  font-weight: 600;
  margin-bottom: 1rem;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
  color: #bbb;
  font-size: 1rem;
  text-align: left;
}

.features-list li {
  margin-bottom: 0.5rem;
}

.package-cta {
  background: #3498DB;
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s ease;
}

.package-cta:hover {
  background: #1A5276;
}

@media (max-width: 768px) {
  .packages-title {
    font-size: 2.5rem;
  }
  .package-card {
    width: 100%;
  }
}

  `}</style>

 <section className="packages-section">
  <h2 className="packages-title">Explore Our Packages</h2>
  <div className="package-cards">
    {packages.map((pkg, idx) => (
      <div
        className="package-card"
        key={idx}
        onClick={() =>
          window.open("https://t.me/DevPrometheus", "_blank") // 👈 opens Telegram handle
        }
      >
        <h3>{pkg.name}</h3>
        <p className="price">{pkg.price}</p>
        <ul className="features-list">
          {pkg.features.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
        <button className="package-cta">{pkg.cta}</button>
      </div>
    ))}
  </div>
</section>
</>

{/* DEMO / TRIAL SECTION */}
<section className="demo-section">
  <h2 className="demo-title">Try A Boost For Free!</h2>
  <p className="demo-text">
    Curious if MOTA really works? Now you can see for yourself – no
    commitment, no risk. <br /><br />
    Our trial runs give you a taste of what full access feels like: real people,
    real engagement, and real results that actually move the needle.
  </p>
  <button
    className="demo-button"
    onClick={() => window.open("hhttps://t.me/DevPrometheus", "_blank")}
  >
    Get Demo Access
  </button>
</section>


          <section
          
            ref={(el) => (sectionRefs.current[1] = el)}
            className="section how-it-works-section"
            id="how-it-works"
          >
            <h2>How It Works</h2>

            <div className="features-container">
              <div className="feature">
                <div className="feature-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="64"
                    height="64"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4S14.21 4 12 4 8 5.79 8 8s1.79 4 4 4zM4 20v-1c0-2.21 3.58-4 8-4s8 1.79 8 4v1"></path>
                  </svg>
                </div>

                <h3 className="feature-title">Humans only</h3>
                <p className="feature-desc">
                  Every like, retweet, and reply comes from real people. No
                  bots, no farms.
                </p>
              </div>

              <div className="feature">
                <div className="feature-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="64"
                    height="64"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path d="M4 14h16M4 10h16M4 6h16M8 6v12M16 6v12"></path>
                  </svg>
                </div>
                <h3 className="feature-title">Customizable</h3>
                <p className="feature-desc">
                  We tailor engagement to your goals. likes, replies, RTs,
                  bookmarks, and private retweets.
                </p>
              </div>

              <div className="feature">
                <div className="feature-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="64"
                    height="64"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="12" cy="12" r="2"></circle>
                    <path d="M5.5 5.5l3 3M18.5 5.5l-3 3M5.5 18.5l3-3M18.5 18.5l-3-3M12 2v2M12 20v2M2 12h2M20 12h2"></path>
                  </svg>
                </div>
                <h3 className="feature-title">Algorithm aware</h3>
                <p className="feature-desc">
                  We activate what matters for reach on X. Timing and mix that
                  favor velocity.
                </p>
              </div>
            </div>
          </section>
          {/* SPECIAL LIMITED OFFER SECTION */}
<section className="offer-section">
  <div className="offer-card">
    <h4 className="offer-subtitle">SPECIAL LIMITED OFFER</h4>
    <h2 className="offer-title">Community and Volume Boost</h2>
    <div className="offer-price">$1,500/day</div>
    <p className="offer-text">
      Get maximum exposure for your project in just one day.
    </p>

    <ul className="offer-list">
      <li> Bring Real People Into Your Community Almost Instantly</li>
      <li>Up to 300 people posting organic content about your project (10+ posts each)</li>
      <li> We track 300+ top KOLs (powered by our partnership with KOLSCAN) and built a system to always be first to reply</li>
      <li> Result: your ticker trends, your narrative spreads, your project gains real traction</li>
      <li> UP TO 500K VOLUME BOOST for your project</li>
    </ul>

    <button
      className="offer-button"
      onClick={() => window.open("https://t.me/DevPrometheus", "_blank")}
    >
      Boost Your Community
    </button>
  </div>
</section>
<FAQSection />

        </section>
      </div>
    </>
  );
};

export default MosaicAgency;
